"use client";

import React, { useMemo } from "react";
import {
  PAYMENT_METHOD,
  type PaymentMethodId,
  getCountryPaymentConfig,
  getMethodDisplay,
} from "@/lib/flutterwave_v4/payment_config";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

interface AlternatePaymentOptionsProps {
  /** ISO country code (e.g. "NG", "GH") */
  countryCode: string;
  /** The method currently being viewed (so we exclude it from alternates) */
  currentMethod?: PaymentMethodId;
  /** Callback: switch to Stripe card checkout */
  onSwitchToStripe?: () => void;
  /** Callback: switch to Bank Transfer */
  onSwitchToBankTransfer?: () => void;
  /** Callback: switch to Mobile Money */
  onSwitchToMomo?: () => void;
  /** Callback: switch to OPay */
  onSwitchToOpay?: () => void;
  /** Whether actions are disabled (e.g. while polling for payment) */
  disabled?: boolean;
}

/**
 * "Having trouble? Try another option:" section.
 *
 * Appears inside every payment detail view (Bank Transfer, MoMo, OPay).
 * Shows only country-eligible alternate methods plus Stripe Card (always shown).
 */
export default function AlternatePaymentOptions({
  countryCode,
  currentMethod,
  onSwitchToStripe,
  onSwitchToBankTransfer,
  onSwitchToMomo,
  onSwitchToOpay,
  disabled = false,
}: AlternatePaymentOptionsProps) {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);
  const config = getCountryPaymentConfig(countryCode);
  const enabledMethods: PaymentMethodId[] = config?.methods ?? [];

  // Build list of alternate methods (excluding the current one)
  const alternateMethods = enabledMethods.filter((m) => m !== currentMethod);

  // Map method → handler
  const handlerMap: Partial<Record<PaymentMethodId, (() => void) | undefined>> = {
    [PAYMENT_METHOD.BANK_TRANSFER]: onSwitchToBankTransfer,
    [PAYMENT_METHOD.MOMO]: onSwitchToMomo,
    [PAYMENT_METHOD.OPAY]: onSwitchToOpay,
  };

  // Map method → button style
  const styleMap: Record<PaymentMethodId, string> = {
    [PAYMENT_METHOD.BANK_TRANSFER]:
      "bg-emerald-600 hover:bg-emerald-500",
    [PAYMENT_METHOD.MOMO]:
      "bg-orange-600 hover:bg-orange-500",
    [PAYMENT_METHOD.OPAY]:
      "bg-purple-600 hover:bg-purple-500",
  };

  // Description text under buttons
  const descriptionMap: Record<PaymentMethodId, string> = {
    [PAYMENT_METHOD.BANK_TRANSFER]: t.transfer_to_bank,
    [PAYMENT_METHOD.MOMO]: t.pay_momo_wallet,
    [PAYMENT_METHOD.OPAY]: t.pay_opay_wallet,
  };

  // Only render if there is at least Stripe or one alternate method
  const hasAlternates = onSwitchToStripe || alternateMethods.some((m) => handlerMap[m]);
  if (!hasAlternates) return null;

  return (
    <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-semibold text-amber-900 mb-3">
        {t.having_trouble}
      </p>

      <div className="space-y-3">
        {/* Stripe Card — always shown */}
        {onSwitchToStripe && (
          <div>
            <button
              onClick={onSwitchToStripe}
              disabled={disabled}
              className="w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.pay_with_card}
            </button>
            <p className="text-xs text-amber-700 mt-1 text-center">
              {t.card_any_country}
            </p>
          </div>
        )}

        {/* Country-eligible alternate methods */}
        {alternateMethods.map((method) => {
          const handler = handlerMap[method];
          if (!handler) return null;
          const display = getMethodDisplay(method);
          const btnStyle = styleMap[method] || "bg-slate-600 hover:bg-slate-500";
          const desc = descriptionMap[method] || display.description;

          return (
            <div key={method}>
              <button
                onClick={handler}
                disabled={disabled}
                className={`w-full rounded-md ${btnStyle} px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {display.emoji} {display.label}
              </button>
              <p className="text-xs text-amber-700 mt-1 text-center">{desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
